import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import { LANGUAGES } from './i18n'

const FLAGS = { en: '🇬🇧', mk: '🇲🇰', sq: '🇦🇱', tr: '🇹🇷' }

/** The browser's language, when it is one we speak and not the default, and nothing was chosen yet. */
function suggestedLanguage() {
  if (localStorage.getItem('doseg.admin.lang')) return null
  const spoken = (navigator.languages ?? [navigator.language]).map((tag) => tag.toLowerCase().split('-')[0])
  const fromLanguage = spoken.find((code) => LANGUAGES.includes(code) && code !== 'en')
  if (fromLanguage) return fromLanguage
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  return { 'Europe/Skopje': 'mk', 'Europe/Tirane': 'sq', 'Europe/Istanbul': 'tr' }[zone] ?? null
}

/** Asked once, in the suggested language's own words; any answer is remembered. */
function LanguageOffer() {
  const { t, i18n } = useTranslation()
  const [suggested, setSuggested] = useState(() => suggestedLanguage())

  if (!suggested) return null

  const choose = (code) => {
    i18n.changeLanguage(code)
    localStorage.setItem('doseg.admin.lang', code)
    setSuggested(null)
  }

  return (
    <div className="langoffer" role="dialog">
      <strong>{t('nav.offerTitle', { lng: suggested })}</strong>
      <span className="langoffer__flags">
        {LANGUAGES.map((code) => (
          <button key={code} type="button" className={`chip ${code === suggested ? 'on' : ''}`} onClick={() => choose(code)}>
            {FLAGS[code]} {code.toUpperCase()}
          </button>
        ))}
      </span>
      <button type="button" className="langoffer__dismiss" onClick={() => choose('en')}>
        {t('nav.keepEnglish', { lng: suggested })}
      </button>
    </div>
  )
}
import Login from './Login.jsx'
import Dashboard from './Dashboard.jsx'
import Costs from './Costs.jsx'
import Tenants from './Tenants.jsx'
import TenantDetail from './TenantDetail.jsx'
import Users from './Users.jsx'
import Messages from './Messages.jsx'
import Plans from './Plans.jsx'

/**
 * Hash routing by hand. A handful of screens do not justify a router dependency, and the hash
 * keeps deep links working without touching nginx: #/tenants, #/tenant/5, #/costs.
 */
const PAGES = ['home', 'tenants', 'users', 'messages', 'costs', 'plans']

function parseHash() {
  const hash = window.location.hash.replace(/^#\/?/, '')
  const [page, id] = hash.split('/')

  if (page === 'tenant' && id) return { page: 'tenant', id: Number(id) }
  return { page: PAGES.includes(page) ? page : 'home' }
}

export default function App() {
  const { t, i18n } = useTranslation()
  const [admin, setAdmin] = useState(null)
  const [checked, setChecked] = useState(false)
  const [route, setRoute] = useState(parseHash)

  useEffect(() => {
    const onHash = () => setRoute(parseHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // A cookie may already be here from last time; asking is cheaper than making a person type.
  useEffect(() => {
    api
      .me()
      .then(setAdmin)
      .catch(() => {})
      .finally(() => setChecked(true))
  }, [])

  const onExpired = useCallback((failure) => {
    if (failure?.unauthenticated) setAdmin(null)
  }, [])

  if (!checked) return <p className="muted center">{t('common.loading')}</p>

  if (!admin) {
    return (
      <>
        <LanguageOffer />
        <Login onSignedIn={setAdmin} />
      </>
    )
  }

  return (
    <div className="shell">
      <LanguageOffer />
      <header className="topbar">
        <img className="topbar__logo" src="/logo.png" alt="Dosegni" />
        {/* The word that says which of the two this is. The mark alone would be the customer
            portal, and the whole point of a separate hostname is not mistaking one for the other. */}
        <span className="topbar__tag">admin</span>
        <span className="muted small topbar__who">{admin.name}</span>

        {/* A select rather than four buttons. Four letters in a row read as navigation and took
            the width the name needed; a dropdown is one control that says what it is. */}
        <select
          className="lang"
          aria-label={t('nav.language')}
          value={i18n.resolvedLanguage}
          onChange={(event) => i18n.changeLanguage(event.target.value)}
        >
          {LANGUAGES.map((code) => (
            <option key={code} value={code}>
              {code.toUpperCase()}
            </option>
          ))}
        </select>

        {/* The queue, opened with the session already in hand — the jobs host reads the same
            cookie. New tab, because leaving this one loses whatever was being looked at. */}
        <a className="button quiet small" href="/hangfire" target="_blank" rel="noreferrer">
          {t('nav.jobs')}
        </a>

        <button
          type="button"
          className="button quiet small"
          onClick={() =>
            api
              .logout()
              .catch(() => {})
              .finally(() => setAdmin(null))
          }
        >
          {t('nav.signOut')}
        </button>
      </header>

      <main>
        {route.page === 'home' && <Dashboard onExpired={onExpired} />}
        {route.page === 'tenants' && <Tenants onExpired={onExpired} />}
        {route.page === 'tenant' && <TenantDetail id={route.id} onExpired={onExpired} />}
        {route.page === 'users' && <Users onExpired={onExpired} />}
        {route.page === 'messages' && <Messages onExpired={onExpired} />}
        {route.page === 'costs' && <Costs onExpired={onExpired} />}
        {route.page === 'plans' && <Plans onExpired={onExpired} />}
      </main>

      {/* One row, however many pages. On a phone it sits under the thumb; on a desk it is a row
          under the heading. One component either way — two navigations would drift. */}
      <nav className="tabs">
        {PAGES.map((page) => (
          <a
            key={page}
            href={`#/${page}`}
            className={route.page === page || (page === 'tenants' && route.page === 'tenant') ? 'active' : ''}
          >
            {t(`nav.${page}`)}
          </a>
        ))}
      </nav>
    </div>
  )
}
